import { DispatchWithoutAction, FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CutterContext } from '../contexts/CutterContext'
import { renderAudioBuffer } from '../export/renderAudioBuffer'
import { downloadAudioFile } from '../export/downloadAudioFile'
import { AudioFileExtension, audioFileExtensions } from '../../enums/AudioFileExtension'
import { useHotkey } from '../../hooks/useHotkey'
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick'
import './ToolbarDownloadPopup.scss'
import { useSuppressCutterHotkeys } from '../../hooks/useSuppressCutterHotkeys'
import { useWavEncoder } from '../export/WavEncoder'
import { ToolbarDownloadSelect } from './ToolbarDownloadSelect'
import { SelectOption } from '../../interfaces/SelectOption'
import { getFilenameExtension } from '../../functions/getFilenameExtension'
import { ToolbarDownloadFilename } from './ToolbarDownloadFilename'
import { ToolbarDownloadLabel } from './ToolbarDownloadLabel'
import { getBaseFilename } from '../../functions/getBaseFilename'
import { useFfmpeg } from '../export/useFfmpeg'
import { logger } from '../../logger/logger'
import { LogActionType } from 'api/logs/log-action-type'
import { getLogAudioBufferInfo } from '../../functions/getLogAudioBufferInfo'
import { getPossibleErrorMessage } from '../../functions/getPossibleErrorMessage'

interface Props {
  close: DispatchWithoutAction
}

const extensionOptions: SelectOption<AudioFileExtension>[] = audioFileExtensions.map((value) => ({
  title: value.toUpperCase(),
  value,
}))

export const ToolbarDownloadPopup: FC<Props> = ({ close }) => {
  const { audioBuffer, audio, filename } = useContext(CutterContext)
  const elementRef = useRef<HTMLDivElement | null>(null)
  const [compiling, setCompiling] = useState(false)
  const wavEncoder = useWavEncoder()
  const ffmpeg = useFfmpeg()

  useSuppressCutterHotkeys()

  useOnOutsideClick(elementRef, close, !compiling)
  useHotkey(({ key }) => key === 'Escape', close, !compiling)

  const [extension, setExtension] = useState<AudioFileExtension>(() => {
    const extension = getFilenameExtension(filename)

    if (audioFileExtensions.includes(extension as AudioFileExtension)) {
      return extension as AudioFileExtension
    }

    return AudioFileExtension.Mp3
  })
  const [bitRate, setBitRate] = useState<number | null>(null)
  const { numberOfChannels } = audioBuffer
  const [channels, setChannels] = useState(numberOfChannels)
  const [name, setName] = useState(() => getBaseFilename(filename))

  const bitRateOptions: SelectOption<number | null>[] = useMemo(
    () =>
      [null, 192, 160, 128, 64].map((value) => ({
        value,
        title: value === null ? 'Auto' : value,
        disabled: (extension === AudioFileExtension.Wav || extension === AudioFileExtension.Flac) && value !== null,
      })),
    [extension],
  )

  const channelOptions: SelectOption<number>[] = useMemo(() => {
    let channelOptions = []

    for (let value = numberOfChannels; value > 0; value--) {
      channelOptions.push({
        title:
          value === 2 ? 'Stereo' : value === 1 ? 'Mono' : value === numberOfChannels ? `${value} (original)` : value,
        value,
      })
    }

    return channelOptions
  }, [numberOfChannels])

  useEffect(() => {
    if (extension === AudioFileExtension.Wav || extension === AudioFileExtension.Flac) {
      setBitRate(null)
    }
  }, [extension])

  const onDownloadClick = async () => {
    setCompiling(true)

    logger.log(LogActionType.DownloadButtonClick)

    renderAudioBuffer({ audioBuffer, audio })
      .then((audioBuffer) => {
        logger.log(LogActionType.DownloadBufferRendered, getLogAudioBufferInfo(audioBuffer))

        return wavEncoder.encode(audioBuffer)
      })
      .then(async (arrayBuffer) => {
        logger.log(LogActionType.DownloadWavEncoded, {
          byteLength: arrayBuffer.byteLength,
        })

        if (extension === AudioFileExtension.Wav && channels === numberOfChannels) {
          downloadAudioFile([arrayBuffer], {
            extension,
            filename: name,
          })
        } else {
          const inputFilename = 'input.wav'
          const outputFilename = 'output.' + extension

          if (!ffmpeg.isLoaded()) {
            await ffmpeg.load()

            logger.log(LogActionType.DownloadFfmpegLoaded)
          }

          ffmpeg.FS('writeFile', inputFilename, new Uint8Array(arrayBuffer))

          const args = ['-i', inputFilename, '-ac', channels.toString()]
          if (bitRate !== null) {
            args.push('-b:a', bitRate + 'k')
          }
          args.push(outputFilename)

          await ffmpeg.run.apply(ffmpeg, args)
          const { buffer } = ffmpeg.FS('readFile', outputFilename)
          ffmpeg.FS('unlink', outputFilename)

          logger.log(LogActionType.DownloadFileConverted, {
            byteLength: buffer.byteLength,
          })

          downloadAudioFile([buffer], {
            extension,
            filename: name,
          })
        }
      })
      .catch((reason: Error) => {
        logger.log(LogActionType.DownloadCompilationError, getPossibleErrorMessage(reason))

        return Promise.reject(reason)
      })
      .finally(() => setCompiling(false))
  }

  return (
    <div className="ToolbarDownloadPopup" ref={elementRef}>
      <ToolbarDownloadLabel>Format</ToolbarDownloadLabel>
      <ToolbarDownloadSelect
        value={extension}
        setValue={(value) => {
          logger.log(LogActionType.DownloadFormatClick, value)

          setExtension(value)
        }}
        options={extensionOptions}
      />

      <ToolbarDownloadLabel>Bitrate (kbps)</ToolbarDownloadLabel>
      <ToolbarDownloadSelect
        value={bitRate}
        setValue={(value) => {
          logger.log(LogActionType.DownloadBitrateClick, value)

          setBitRate(value)
        }}
        options={bitRateOptions}
      />

      <ToolbarDownloadLabel>Channels</ToolbarDownloadLabel>
      <ToolbarDownloadSelect
        value={channels}
        setValue={(value) => {
          logger.log(LogActionType.DownloadChannelsClick, value)

          setChannels(value)
        }}
        options={channelOptions}
      />

      <ToolbarDownloadLabel>File name</ToolbarDownloadLabel>
      <ToolbarDownloadFilename
        value={name}
        setValue={setName}
        onBlur={(value) => logger.log(LogActionType.DownloadFilenameChange, value)}
        extension={extension}
      />

      <button className="submit" type="button" onClick={onDownloadClick} disabled={compiling} autoFocus={true}>
        {compiling ? 'Compiling...' : 'Download'}
      </button>
    </div>
  )
}
